import React, { useState } from 'react';
import { useOptionalWebSocket } from '../../../contexts/WebSocketContext';
import './AuthoritativeDamageControls.css';

const DAMAGE_TYPES = ['acid', 'bludgeoning', 'cold', 'fire', 'force', 'lightning', 'necrotic', 'piercing', 'poison', 'psychic', 'radiant', 'slashing', 'thunder', 'untyped'];

const AuthoritativeDamageControls = ({ token, isGameMaster = false }) => {
  const { currentSessionId, sendBattleRoomMutation } = useOptionalWebSocket() || {};
  const [operation, setOperation] = useState('damage');
  const [amount, setAmount] = useState('');
  const [damageType, setDamageType] = useState('untyped');
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  if (!isGameMaster || !token) return null;
  const numericAmount = Number(amount);
  const valid = Number.isInteger(numericAmount) && numericAmount > 0 && numericAmount <= 100000
    && (operation === 'healing' || !override || reason.trim());

  const applyHitPointChange = async (event) => {
    event.preventDefault();
    if (!valid || typeof sendBattleRoomMutation !== 'function') return;
    setPending(true);
    setError('');
    setMessage('');
    try {
      const response = operation === 'healing'
        ? await sendBattleRoomMutation('healing:apply', { sourceTokenId: token.id, targetId: token.id, amount: numericAmount })
        : await sendBattleRoomMutation('damage:apply', {
            sourceTokenId: token.id,
            targetId: token.id,
            components: [{ componentId: `manual:${Date.now()}`, damageType, rolledAmount: numericAmount }],
            ...(override ? { gmOverride: { enabled: true, reason: reason.trim() } } : {}),
          });
      const resolution = response?.result?.resolution;
      const applied = operation === 'healing' ? resolution?.applied : resolution?.totalApplied;
      setMessage(`${applied ?? numericAmount} ${operation === 'healing' ? 'healing' : 'damage'} applied. HP ${resolution?.hpBefore ?? '?'} → ${resolution?.hpAfter ?? '?'}.`);
      setAmount('');
      if (override) setReason('');
    } catch (cause) {
      setError(cause?.message || 'The hit point change was not applied.');
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="br-authoritative-damage" aria-labelledby={`hp-change-${token.id}`}>
      <h4 id={`hp-change-${token.id}`}>Adjust hit points</h4>
      <p>Damage and healing are committed by the server. Damage uses the token’s structured immunity, resistance, and vulnerability data.</p>
      {!currentSessionId && <p role="status">Join a live BattleRoom to adjust hit points.</p>}
      <form onSubmit={applyHitPointChange}>
        <label>Operation<select value={operation} onChange={(event) => { setOperation(event.target.value); setOverride(false); }}><option value="damage">Damage</option><option value="healing">Healing</option></select></label>
        <label>{operation === 'healing' ? 'Healing amount' : 'Rolled amount'}<input type="number" min="1" max="100000" step="1" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
        {operation === 'damage' && <label>Damage type<select value={damageType} onChange={(event) => setDamageType(event.target.value)}>{DAMAGE_TYPES.map((type) => <option key={type} value={type}>{type[0].toUpperCase() + type.slice(1)}</option>)}</select></label>}
        {operation === 'damage' && <label className="br-authoritative-damage__check"><input type="checkbox" checked={override} onChange={(event) => setOverride(event.target.checked)} /> Game Master override (bypass mitigation)</label>}
        {operation === 'damage' && override && <label>Required audit reason<textarea rows={2} maxLength={240} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why should mitigation be bypassed?" /></label>}
        <button type="submit" disabled={!currentSessionId || pending || !valid}>{pending ? 'Applying…' : operation === 'healing' ? 'Apply Healing' : override ? 'Apply Override Damage' : 'Apply Damage'}</button>
      </form>
      {message && <p className="br-authoritative-damage__success" role="status">{message}</p>}
      {error && <p className="br-authoritative-damage__error" role="alert">{error}</p>}
    </section>
  );
};

export default AuthoritativeDamageControls;
